import { inject, Injectable } from '@angular/core';
import { BearerScheme } from '@core/enums/http-requests.enum';
import { RedirectService } from '@core/services/redirect.service';
import { TokenStore } from '@core/services/token.store';
import { StorageServiceKeysMap } from '@core/storage/types/storage.types';
import { RedirectStrategyTypeMap } from '@core/types/redirect.types';
import { AuthorizationFacade } from '@features/authorization/facades/authorization.facade';
import type { ResponseAuthenticationDto } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/dtos/response.authentication.dto';
import { AuthenticationMessageMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/enums/authentication-message.enum';
import { LoggerService, StorageService } from '@kerberos-compliance/compliance-shared-ui';
import { UserSessionFacade } from '../facades/user-session.facade';

const E2ETokenExpirySeconds = 3600;

type E2EAuthState = {
  accessToken: string;
  refreshToken: string;
  isAuthenticated: boolean;
  authenticatedUser: unknown;
};

/**
 * Handles the full E2E authentication flow: reads tokens from storage, initializes
 * the user session, and redirects. Used only in non-MSAL (E2E / dev) environments.
 */
@Injectable({ providedIn: 'root' })
export class E2EAuthenticationService {
  private readonly loggerService = inject(LoggerService);

  private readonly storageService = inject(StorageService);

  private readonly tokenStore = inject(TokenStore);

  private readonly authorizationFacade = inject(AuthorizationFacade);

  private readonly userSessionFacade = inject(UserSessionFacade);

  private readonly redirectService = inject(RedirectService);

  /**
   * Reads E2E tokens from storage, initializes the user session, and redirects.
   * No-ops silently if no valid E2E auth state is found.
   *
   * @returns {Promise<void>} A promise that resolves when the E2E auth flow completes.
   */
  public async handleE2EAuthentication(): Promise<void> {
    const session = await this.getSessionFromStorage();
    if (!session) return;
    this.tokenStore.setTokens(session.access_token, session.refresh_token);
    await this.authorizationFacade.loadGrants();
    this.userSessionFacade.startSession();
    this.redirectService.handleRedirect(RedirectStrategyTypeMap.AzureLogin);
    this.loggerService.debug('E2E authentication successful', 'E2EAuthenticationService');
  }

  /**
   * Reads and validates the E2E authentication state from storage.
   *
   * @returns {Promise<ResponseAuthenticationDto | undefined>} Parsed session DTO, or undefined if absent or invalid.
   */
  private async getSessionFromStorage(): Promise<ResponseAuthenticationDto | undefined> {
    try {
      const state = this.storageService.get<unknown>(StorageServiceKeysMap.AuthenticationStorageKey);
      if (!this.isE2EAuthState(state) || !state.isAuthenticated) {
        return undefined;
      }

      return {
        access_token: state.accessToken,
        refresh_token: state.refreshToken,
        token_type: BearerScheme,
        expires_in: E2ETokenExpirySeconds,
        message: AuthenticationMessageMap.Success,
      };
    } catch (error) {
      this.loggerService.warn('E2E authentication failed', 'E2EAuthenticationService', { error });
      return undefined;
    }
  }

  /**
   * Determines if the provided candidate matches the E2EAuthState structure.
   *
   * @param {unknown} candidate - The object to evaluate.
   * @returns {boolean} True if the candidate is of type E2EAuthState.
   */
  private isE2EAuthState(candidate: unknown): candidate is E2EAuthState {
    if (typeof candidate !== 'object' || candidate === null) return false;
    const { accessToken, refreshToken, isAuthenticated } = candidate as Record<string, unknown>;
    return typeof accessToken === 'string' && typeof refreshToken === 'string' && typeof isAuthenticated === 'boolean';
  }
}
